<template>
    <div class="sms-broadcast-item">
        <div class="item-entry data-entry" @click="displayItem">
            <small class="sms-broadcast-label">
                {{ $options.MESSAGE_CONSTANTS.TEXT }}
            </small>
            <span
                class="stat-subject"
                :title="title"
            >
                {{ title }}
            </span>
            <div class="sms-broadcast-date-message">
                <span>{{ broadcastDateMessage }}</span>
            </div>
        </div>

        <div class="item-entry status-entry" @click="displayItem">
            <ds-chip :class="['chip', `${broadcast.status}`]">
                {{ broadcastStatusMessage }}
            </ds-chip>
        </div>

        <div class="item-entry stat-entry" @click="displayItem">
            <span class="stat-field"> {{ sentStatMessage }} </span>
            <span
                v-show="hasStatistics"
                :class="['sms-broadcast-stats-label', `${broadcast.status}`]"
            >
                {{ $options.MESSAGE_CONSTANTS.SENT }}
            </span>
        </div>

        <div class="item-entry stat-entry" @click="displayItem">
            <span class="stat-field">{{ pendingStatMessage }}</span>
            <span
                v-show="hasStatistics"
                :class="['sms-broadcast-stats-label', `${broadcast.status}`]"
            >
                {{ $options.MESSAGE_CONSTANTS.PENDING }}
            </span>
        </div>

        <div class="item-entry stat-entry" @click="displayItem">
            <span class="stat-field">{{ unsubscribedStatMessage }}</span>
            <span
                v-show="hasStatistics"
                :class="['sms-broadcast-stats-label', `${broadcast.status}`]"
            >
                {{ $options.MESSAGE_CONSTANTS.UNSUBSCRIBED }}
            </span>
        </div>

        <div class="item-entry options-entry">
            <SmsBroadcastOptions
                :key="broadcast.id"
                :schedule-date="broadcast.scheduleDate"
                :status="broadcast.status"
                @copy="copyBroadcast"
                @delete="deleteBroadcast"
                @edit="editBroadcast"
                @stop="stopBroadcast"
                @resume="resumeBroadcast"
            />
        </div>
    </div>
</template>

<script>
import { SMS_BROADCAST_STATUS, SMS_BROADCAST_STATUS_MESSAGE, MESSAGE_CONSTANTS } from '../constants/smsBroadcast.constants';
import { shortDateFormat } from '../utils/date.util';
import SmsBroadcastOptions from '@/components/broadcast/sms/SmsBroadcastOptions';
import { showEditForStoppedBroadcast } from '@/components/broadcast/utils/smsBroadcast.util';

export default {

    SMS_BROADCAST_STATUS,
    SMS_BROADCAST_STATUS_MESSAGE,
    MESSAGE_CONSTANTS,
    shortDateFormat,

    components: {
        SmsBroadcastOptions,
    },

    props: {
        broadcast: Object,
    },

    computed: {
        title() {
            return this.broadcast.title || '(no title)';
        },

        broadcastStatusMessage() {
            let { status } = this.broadcast;
            const { DRAFT, SCHEDULED, SENDING } = SMS_BROADCAST_STATUS;

            if (status == null) {
                status = DRAFT;
            }

            if (status === SCHEDULED && this.isScheduleDateInPast) {
                status = SENDING;
            }

            return SMS_BROADCAST_STATUS_MESSAGE[status];
        },

        broadcastDateMessage() {
            const {
                updated,
                scheduleDate,
                status,
            } = this.broadcast;

            let message;
            const action = SMS_BROADCAST_STATUS_MESSAGE[status];

            switch (status) {
            case (SMS_BROADCAST_STATUS.SCHEDULED):
                if (scheduleDate && !this.isScheduleDateInPast) {
                    message = `${action} ${shortDateFormat(scheduleDate)}`;
                } else {
                    message = MESSAGE_CONSTANTS.BROADCAST_SENDING_MESSAGE;
                }
                break;
            case (SMS_BROADCAST_STATUS.SENDING):
                message = action;
                break;
            case (SMS_BROADCAST_STATUS.SENT):
                message = `${action} ${shortDateFormat(updated)}`;
                break;
            case (SMS_BROADCAST_STATUS.STOPPED):
                message = `${action} ${shortDateFormat(updated)}`;
                break;
            case (SMS_BROADCAST_STATUS.ERROR):
                message = MESSAGE_CONSTANTS.BROADCAST_ERROR_MESSAGE;
                break;
            default:
                message = `Edited ${shortDateFormat(updated)}`;
                break;
            }

            return message;
        },

        isScheduleDateInPast() {
            return showEditForStoppedBroadcast(this.broadcast.scheduleDate);
        },

        hasStatistics() {
            return !!this.broadcast.smsBroadcastStatistics;
        },

        sentStatMessage() {
            let response = '-';

            if (this.hasStatistics) {
                response = this.broadcast.smsBroadcastStatistics.sent;

                if (this.broadcast.smsBroadcastStatistics.sent > 0) {
                    response = this.calculateStat(this.broadcast.smsBroadcastStatistics.sent,
                        this.broadcast.smsBroadcastStatistics.total);
                }
            }

            return response;
        },

        pendingStatMessage() {
            let response = '-';

            if (this.hasStatistics) {
                response = this.broadcast.smsBroadcastStatistics.pendingOptIn;

                if (this.broadcast.smsBroadcastStatistics.pendingOptIn > 0) {
                    response = this.calculateStat(this.broadcast.smsBroadcastStatistics.pendingOptIn,
                        this.broadcast.smsBroadcastStatistics.total);
                }
            }

            return response;
        },

        unsubscribedStatMessage() {
            let response = '-';

            if (this.hasStatistics) {
                response = this.broadcast.smsBroadcastStatistics.unsubscribed;

                if (this.broadcast.smsBroadcastStatistics.unsubscribed > 0) {
                    response = this.calculateStat(this.broadcast.smsBroadcastStatistics.unsubscribed,
                        this.broadcast.smsBroadcastStatistics.total);
                }
            }

            return response;
        },
    },

    methods: {
        displayItem() {
            this.$emit('open', this.broadcast);
        },

        copyBroadcast() {
            this.$emit('copy', this.broadcast.id);
        },

        stopBroadcast() {
            this.$emit('stop', this.broadcast.id);
        },

        resumeBroadcast() {
            this.$emit('resume', this.broadcast.id);
        },

        deleteBroadcast() {
            this.$emit('delete', this.broadcast.id);
        },

        editBroadcast() {
            this.$emit('edit', this.broadcast.id);
        },

        calculateStat(percent, total) {
            const stat = ((percent / total) * 100).toFixed();

            return `${percent} (${stat}%)`;
        },
    },
};
</script>

<style lang="scss">
    div.sms-broadcast-item > div.item-entry {
        height: 100%;
        width: 100%;
    }

    div.sms-broadcast-item ul.tabs {
        border-bottom: none;
    }

    div.sms-broadcast-item {
        transition: background-color .25s cubic-bezier(.23,1,.32,1);
        color: inherit;
        text-decoration: none;
        padding: 1rem;
        grid-gap: 1rem;
        width: 100%;
        display: inline-grid;
        grid-template-columns: 7fr 2fr 2fr 2fr 2fr 1fr;
        align-items: center;
        border-bottom: 1px solid #e7e7e7;
    }

    div.sms-broadcast-item:hover {
        background-color: $color-gray-050;
        cursor: pointer;
    }

    div.sms-broadcast-item > div.data-entry {
        display: inline-grid;
    }

    div.sms-broadcast-item > div.stat-entry,
    div.sms-broadcast-item > div.status-entry,
    div.sms-broadcast-item > div.options-entry {
        display: inline-grid;
        align-items: center;
    }

    div.sms-broadcast-item > div.status-entry {
        display: flex;
    }

    .sms-broadcast-item .sms-broadcast-stats-label {
        color: $color-ink-800;
        font-size: .75rem;
    }

    div.sms-broadcast-item div.chip.STOPPED,
    div.sms-broadcast-item div.chip.ERROR {
        background-color: $color-red-050;
    }

    div.sms-broadcast-item div.chip.SCHEDULED,
    div.sms-broadcast-item div.chip.SENDING {
        background-color: $color-orange-050;
    }

    div.sms-broadcast-item div.chip.SENT {
        background-color: $color-green-050;
    }

    div.sms-broadcast-item small.sms-broadcast-label {
        font-size: .75rem;
        color: $color-ink-800;
    }

    div.sms-broadcast-item span.stat-subject {
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 1rem;
        padding-bottom: .25rem;
    }

    div.sms-broadcast-item div.sms-broadcast-date-message > span {
        font-size: .875rem;
        color: $color-ink-800;
    }
</style>
